import React, { useEffect, useMemo, useRef } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { Button, Input, Loading, Popup } from 'src/components'
import { useStyles } from './styles'
import { ClassificationSelect } from '../ClassificationSelect'
import { VALIDATIONS } from 'src/@types/validations'
import { PastureForm } from 'src/@types/validations/Local'
import { useAuth } from 'src/contexts/auth'
import { parseBRNumber } from 'src/utils/numberMask'
import { useGetPastureById } from 'src/hooks/api/pasture/useGetPastureById'
import { useUpdatePasture } from 'src/hooks/api/pasture/mutations/useUpadePasture'
import { useQueryClient } from '@tanstack/react-query'
import { useGetPastureList } from 'src/hooks/api/pasture'

interface PastureModalProps {
  pastureId?: string
  isOpen: boolean
  onClose: () => void
}

export const PastureModal: React.FC<PastureModalProps> = ({ pastureId, isOpen, onClose }) => {
  const { userFarm: farmId } = useAuth()
  const style = useStyles()
  const formRef = useRef<HTMLFormElement>(null)
  const queryClient = useQueryClient()

  const { data: pastureList } = useGetPastureList()
  const pastureListData = useMemo(() => {
    if (!pastureList) return []

    return pastureList.filter((item) => item.acao !== 'Removido')
  }, [pastureList])

  const { data: locationData, isLoading, isError, refetch, isRefetching } = useGetPastureById(pastureId!)
  const { mutateAsync: updatePasture } = useUpdatePasture()

  const isLoadingData = isLoading || isRefetching

  const {
    setValue,
    clearErrors,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<PastureForm>({
    resolver: yupResolver(VALIDATIONS.Local),
    defaultValues: {
      idFazenda: farmId ?? '',
      descricao: '',
      area: null,
      classificacao: null,
      pasto: 'good',
      cerca: 'good',
      aguados: 'good',
      saleiros: 'good'
    },
    context: {
      currentName: locationData?.descricao,
      existingNames: pastureListData?.map((p) => p.descricao) || []
    }
  })

  useEffect(() => {
    const data = locationData as any
    if (data) {
      setValue('idPasto', data.idPasto)
      setValue('descricao', data.descricao)
      setValue('area', data.area)
      setValue('pasto', data.pasto || 'good')
      setValue('cerca', data.cerca || 'good')
      setValue('aguados', data.aguados || 'good')
      setValue('saleiros', data.saleiros || 'good')
    }
  }, [locationData, setValue])

  const onSubmit = async (data: PastureForm) => {
    await updatePasture(data)

    queryClient.invalidateQueries({ queryKey: ['getPastureList', farmId] })
    queryClient.invalidateQueries({ queryKey: ['getPastureById', farmId, pastureId] })
    onClose()
  }

  return (
    <Popup
      title={`${pastureId ? 'Editar' : 'Cadastrar'} Local`}
      maxWidth={1200}
      isOpen={!!isOpen}
      onClose={onClose}
      closeOnClickOutside={false}
      footer={() => (
        <div className={style.modalFooter}>
          <Button type='button' outlined onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type='button'
            onClick={() => formRef.current?.requestSubmit()}
            disabled={isLoadingData || isError || isSubmitting}
            isLoading={isSubmitting}
          >
            Salvar
          </Button>
        </div>
      )}
    >
      <form ref={formRef} className={style.updatePastureContainer} onSubmit={handleSubmit(onSubmit)}>
        {isLoadingData || isError ? (
          <div className={style.loadingContainer}>
            <Loading isError={isError} refetch={refetch} />
          </div>
        ) : (
          <>
            <Input
              id='updatePasture'
              label='Nome do Local'
              placeholder='Nome do Local'
              value={watch('descricao')}
              error={errors?.descricao?.message}
              className={style.updatePastureInput}
              onChange={(value: string) => {
                setValue('descricao', value)
                clearErrors('descricao')
              }}
            />
            <Input
              label='Área do Local (ha)'
              value={watch('area')}
              placeholder='Em hectares'
              error={errors?.area?.message}
              className={style.updatePastureInput}
              onChange={(value: string) => {
                setValue('area', parseBRNumber(value))

                clearErrors('area')
              }}
            />
            <div className={style.updatePastureClassificationContainer}>
              <ClassificationSelect
                label='Condição do Pasto'
                selectedValue={watch('pasto')}
                onSelect={(val) => {
                  setValue('pasto', val)
                  clearErrors('pasto')
                }}
              />

              <ClassificationSelect
                label='Condição da Cerca'
                selectedValue={watch('cerca')}
                onSelect={(val) => {
                  setValue('cerca', val)
                  clearErrors('cerca')
                }}
              />
              <ClassificationSelect
                label='Condição das Aguadas'
                selectedValue={watch('aguados')}
                onSelect={(val) => {
                  setValue('aguados', val)
                  clearErrors('aguados')
                }}
              />
              <ClassificationSelect
                label='Condição dos Saleiros'
                selectedValue={watch('saleiros')}
                onSelect={(val) => {
                  setValue('saleiros', val)
                  clearErrors('saleiros')
                }}
              />
            </div>
          </>
        )}
      </form>
    </Popup>
  )
}
